"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import { uploadVoiceClone } from "@/lib/narration";
import styles from "./NarrationPanel.module.css";

/**
 * VoiceCloneWizard — 3-step "내 목소리 학습" flow.
 *
 * Mockup source: docs/mockups/narration.html panel 25-A7 (CTA only —
 * wizard itself is a new modal designed for Task 13).
 *
 * Step 1: upload 30 ~ 60 second sample audio
 * Step 2: name the clone voice
 * Step 3: simulated training progress → "사용하기"
 *
 * Uses createPortal to mount to <body>. Closing via × button, ESC,
 * or backdrop click. Calls onTrained with the cloned name once the
 * fake progress reaches 100 %.
 */

const STEPS = ["업로드", "이름 지정", "학습 / 사용"] as const;

export interface VoiceCloneWizardProps {
	open: boolean;
	onClose: () => void;
	onTrained?: (name: string) => void;
}

export function VoiceCloneWizard({
	open,
	onClose,
	onTrained,
}: VoiceCloneWizardProps) {
	const router = useRouter();
	const [mounted, setMounted] = useState(false);
	const [step, setStep] = useState(0);
	const [sampleName, setSampleName] = useState("");
	const [sampleFile, setSampleFile] = useState<File | null>(null);
	const [voiceName, setVoiceName] = useState("내 목소리");
	const [progress, setProgress] = useState(0);
	const [training, setTraining] = useState(false);
	const [trainError, setTrainError] = useState<string | null>(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) {
			// Reset when closed
			setStep(0);
			setSampleName("");
			setSampleFile(null);
			setVoiceName("내 목소리");
			setProgress(0);
			setTraining(false);
			setTrainError(null);
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	const handleUpload = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const f = e.target.files?.[0];
			if (f) {
				setSampleName(f.name);
				setSampleFile(f);
			}
		},
		[],
	);

	/**
	 * Real upload — POST the sample audio to the Worker's /voice-clone/upload.
	 * Before this the wizard faked progress with a setInterval counter and the
	 * uploaded file's bytes were never sent anywhere.
	 */
	const handleStartTraining = useCallback(async () => {
		if (!sampleFile) {
			setTrainError("업로드한 오디오 샘플이 없습니다.");
			return;
		}
		setTrainError(null);
		setProgress(15);
		setTraining(true);
		const res = await uploadVoiceClone(sampleFile, voiceName.trim());
		if (!res.ok) {
			setTraining(false);
			setProgress(0);
			if (res.status === 401) {
				router.push("/login");
				return;
			}
			if (res.error === "insufficient_credits") {
				setTrainError(
					`코인이 부족합니다 (보유 ${res.balance ?? 0} · 필요 ${res.required ?? "?"}).`,
				);
				return;
			}
			setTrainError(`음성 학습 실패: ${res.error ?? "알 수 없는 오류"}`);
			return;
		}
		setProgress(100);
		setTraining(false);
	}, [sampleFile, voiceName, router]);

	const handleFinish = useCallback(() => {
		onTrained?.(voiceName.trim() || "내 목소리");
		onClose();
	}, [onTrained, onClose, voiceName]);

	if (!open || !mounted) return null;

	return createPortal(
		<div
			className={styles.wizardBackdrop}
			role="presentation"
			onClick={onClose}
			data-testid="clone-wizard-backdrop"
		>
			<div
				className={styles.wizardModal}
				role="dialog"
				aria-modal="true"
				aria-label="Voice Clone 마법사"
				data-testid="clone-wizard"
				onClick={(e) => e.stopPropagation()}
				{...devLabelPanel("25-A-wizard")}
			>
				<header className={styles.wizardHeader}>
					<h2 className={styles.wizardTitle}>내 목소리 학습</h2>
					<button
						type="button"
						className={styles.wizardClose}
						onClick={onClose}
						aria-label="닫기"
						data-testid="clone-wizard-close"
					>
						×
					</button>
				</header>

				<ol className={styles.wizardSteps} aria-label="진행 단계">
					{STEPS.map((label, i) => (
						<li
							key={label}
							className={`${styles.wizardStep} ${i === step ? styles.wizardStepOn : ""} ${i < step ? styles.wizardStepDone : ""}`}
							data-testid={`clone-step-indicator-${i}`}
						>
							<span className={styles.wizardStepNum}>{i + 1}</span>
							<span className={styles.wizardStepLabel}>{label}</span>
						</li>
					))}
				</ol>

				<div className={styles.wizardBody}>
					{step === 0 && (
						<div data-testid="clone-step-0" {...devLabel("25-A-wizard-1")}>
							<p className={styles.wizardLead}>
								30 ~ 60 초 분량의 깨끗한 음성 샘플을 업로드 하세요. 배경음
								없이 또렷한 발음을 권장합니다.
							</p>
							<label className={styles.wizardUpload}>
								<input
									type="file"
									accept="audio/*"
									onChange={handleUpload}
									data-testid="clone-upload-input"
								/>
								<span>
									{sampleName ? sampleName : "오디오 파일 선택 (.wav / .mp3)"}
								</span>
							</label>
						</div>
					)}

					{step === 1 && (
						<div data-testid="clone-step-1" {...devLabel("25-A-wizard-2")}>
							<p className={styles.wizardLead}>
								Voice 카탈로그에서 보일 이름을 정해주세요.
							</p>
							<label className={styles.wizardField}>
								<span className={styles.wizardFieldLabel}>Voice 이름</span>
								<input
									type="text"
									value={voiceName}
									maxLength={20}
									onChange={(e) => setVoiceName(e.target.value)}
									placeholder="예: 내 목소리"
									data-testid="clone-name-input"
								/>
							</label>
						</div>
					)}

					{step === 2 && (
						<div data-testid="clone-step-2" {...devLabel("25-A-wizard-3")}>
							<p className={styles.wizardLead}>
								업로드한 샘플로 {voiceName} 음성 모델을 학습합니다. 약 1 분
								소요됩니다.
							</p>
							<div className={styles.wizardProgress}>
								<div
									className={styles.wizardProgressBar}
									style={{ width: `${progress}%` }}
									role="progressbar"
									aria-valuenow={progress}
									aria-valuemin={0}
									aria-valuemax={100}
									data-testid="clone-progress"
								/>
							</div>
							<div className={styles.wizardProgressLabel}>
								{trainError
									? "학습 실패"
									: !training && progress === 0
										? "대기 중"
										: progress >= 100
											? "학습 완료"
											: training
												? "업로드 / 학습 중…"
												: `학습 중 ${progress} %`}
							</div>
							{trainError && (
								<div
									data-testid="clone-train-error"
									role="alert"
									style={{
										marginTop: 8,
										padding: "8px 10px",
										borderRadius: 6,
										border: "1px solid #ef4444",
										background: "rgba(239,68,68,0.08)",
										color: "#ef4444",
										fontSize: 11,
										lineHeight: 1.5,
									}}
								>
									{trainError}
								</div>
							)}
						</div>
					)}
				</div>

				<div className={styles.wizardActions}>
					<button
						type="button"
						className={styles.wizardBtnGhost}
						onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
						data-testid="clone-prev"
					>
						{step === 0 ? "취소" : "이전"}
					</button>
					{step < 2 && (
						<button
							type="button"
							className={styles.wizardBtnPrimary}
							onClick={() => setStep(step + 1)}
							disabled={
								(step === 0 && !sampleName) ||
								(step === 1 && !voiceName.trim())
							}
							data-testid="clone-next"
						>
							다음
						</button>
					)}
					{step === 2 && !training && progress === 0 && (
						<button
							type="button"
							className={styles.wizardBtnPrimary}
							onClick={handleStartTraining}
							data-testid="clone-start-training"
						>
							학습 시작 (5 코인)
						</button>
					)}
					{step === 2 && progress >= 100 && (
						<button
							type="button"
							className={styles.wizardBtnPrimary}
							onClick={handleFinish}
							data-testid="clone-finish"
						>
							사용하기
						</button>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
